import { Link, useParams } from 'react-router';
import { PageWrapper, SuiTable } from '@britishcouncil/react-admin';
import { Button } from 'semantic-ui-react';
import { useLocalStorage, usePermissions } from '@britishcouncil/react-common';

import { TableState } from 'types';
import { useTableState } from 'hooks';
import { ROUTING_PATHS } from 'const';
import { ExaminerListFilterType, useGetExaminerListRecordsQuery } from 'store';
import {
  CentreTypeSelector,
  DetailedCertificationType,
  ExaminerDto,
  ExaminerListType,
  Permission,
} from 'api';
import { columns } from './ExaminerListColumns';
import { ExaminerListFiltersBar } from './ExaminerListFilters';

const LIST_TYPE_STORAGE_KEYS: Record<ExaminerListType, string> = {
  [ExaminerListType.General]: 'ExaminerQueryFilter',
  [ExaminerListType.CertificationExpired]: 'CertificationExpiredQueryFilter',
  [ExaminerListType.CertificationExpireSoon]: 'CertificationExpireSoonQueryFilter',
  [ExaminerListType.MonitoringExpired]: 'MonitoringExpiredQueryFilter',
  [ExaminerListType.MonitoringExpireSoon]: 'MonitoringExpireSoonQueryFilter',
  [ExaminerListType.TrainingNotCompleted]: 'TrainingNotCompletedQueryFilter',
  [ExaminerListType.TrainingUpcoming]: 'TrainingUpcomingQueryFilter',
};

type ExaminerListProps = {
  listType: ExaminerListType;
};

export const ExaminerList = ({ listType }: ExaminerListProps) => {
  const { certificationType } = useParams<{ certificationType?: string }>();
  const { permissions } = usePermissions();
  const canAddExaminer = permissions.includes(Permission.AddNewExaminer);

  const { storedValue: localStorageFilters, setValue: saveFiltersToStorage } =
    useLocalStorage<ExaminerListFilterType>(LIST_TYPE_STORAGE_KEYS[listType]);

  const defaultState = getDefaultTableState(listType, certificationType);
  const initialFiltersValues = { ...defaultState.filters, ...localStorageFilters };

  const { tableState, setTableState, ...tableStateMethods } = useTableState({
    ...defaultState,
    filters: initialFiltersValues,
  });

  const setFilterState = (filters: ExaminerListFilterType) => {
    saveFiltersToStorage(filters);
    tableStateMethods.setFilterState(filters);
  };

  const { filters: _filters, ...pagingAndSortingState } = tableState;

  const { data, isFetching } = useGetExaminerListRecordsQuery(tableState, {
    refetchOnMountOrArgChange: true,
  });

  return (
    <PageWrapper
      mainActions={
        canAddExaminer && listType === ExaminerListType.General ? (
          <Button as={Link} to={ROUTING_PATHS.examiners.add.to} primary>
            Add new examiner
          </Button>
        ) : undefined
      }
      mainActionsAbove
    >
      <ExaminerListFiltersBar
        setFilterState={setFilterState}
        initialFilterState={initialFiltersValues}
        defaultFilterState={defaultState.filters}
      />
      <SuiTable
        columns={columns}
        data={data?.results ?? []}
        disableFilters
        onStateChange={setTableState}
        loading={isFetching}
        state={pagingAndSortingState}
        totalCount={data?.totalCount}
        cellWhiteSpace="normal"
        tableProps={{ 'data-testid': 'examiner-list-table' }}
      />
    </PageWrapper>
  );
};

function getCertificationTypeFromShortCode(
  shortCode?: string
): DetailedCertificationType | undefined {
  switch (shortCode?.toLowerCase()) {
    case 'speaking':
      return DetailedCertificationType.Speaking;
    case 'writing':
      return DetailedCertificationType.Writing;
    case 'lifeskills':
      return DetailedCertificationType.LifeSkills;
    case 'speakingonline':
      return DetailedCertificationType.SpeakingOnline;
    default:
      return undefined;
  }
}

const getDefaultTableState = (
  listType: ExaminerListType,
  certificationTypeShortCode?: string
): TableState<ExaminerDto, ExaminerListFilterType> => ({
  pageIndex: 0,
  pageSize: 50,
  sortBy: [{ id: 'lastName', desc: false }],
  filters: {
    centreType: CentreTypeSelector.BC,
    listType: listType !== ExaminerListType.General ? listType : undefined,
    detailedCertificationType: getCertificationTypeFromShortCode(certificationTypeShortCode),
  },
});
